import request from '@/api/request'
import { clearDictionaryCache } from '@/utils/dictionaryCache'

export interface Customer {
  id: number
  name: string
  phone?: string
  type: 'individual' | 'company'
  level: 'A' | 'B' | 'C' | 'D'
  stage: 'lead' | 'contact' | 'intention' | 'quotation' | 'deal' | 'aftercare' | 'lost'
  source?: string
  address?: string
  gpsLat?: number
  gpsLng?: number
  salesId?: number
  salesName?: string
  remark?: string
  // [ADDED 2026-07-13] 商机管理字段（原opportunities表功能合并至此）
  expectedAmount?: number
  expectedCloseDate?: string
  probability?: number
  dealCount?: number
  lastDealAt?: string
  lastFollowUpAt?: string
  avatar?: string
  isActive?: boolean
  referrerId?: number | null
  referrerName?: string
  referrerDefaultRate?: number | null
  /** 客户提成比例（%）：按收款计提模式下，收款金额 × 该比例 × 员工占比计提提成；null/空=不计提 */
  commissionRate?: number | null
  createdAt?: string
  updatedAt?: string
}

export interface CustomerQuery {
  page?: number
  pageSize?: number
  keyword?: string
  type?: string
  level?: string
  stage?: string
  salesId?: number
  source?: string
  referrerId?: number
  startDate?: string
  endDate?: string
  amountMin?: number
  amountMax?: number
  sortBy?: 'id' | 'createdAt' | 'updatedAt' | 'name' | 'phone' | 'type' | 'salesName' | 'level' | 'stage' | 'source'
  sortOrder?: 'asc' | 'desc'
}

export interface Contact {
  id: number
  customerId: number
  name: string
  phone?: string
  email?: string
  position?: string
  isPrimary?: boolean
  createdAt?: string
}

export interface FollowUp {
  id: number
  customerId: number
  employeeId: number
  employeeName?: string
  type: 'phone' | 'visit' | 'wechat' | 'email' | 'other'
  content?: string
  result?: string | null
  status?: 'pending' | 'completed' | 'cancelled'
  images?: string[]
  nextFollowUpDate?: string
  reminderSet?: boolean
  createdAt?: string
}

export interface CustomerDetail extends Customer {
  contacts: Contact[]
  followUps: FollowUp[]
  // 协作销售（含主归属）
  salesParticipants?: { employeeId: number; employeeName: string }[]
}

export interface CustomerListResponse {
  list: Customer[]
  pagination: {
    page: number
    pageSize: number
    total: number
  }
}

export function getCustomerList(params?: CustomerQuery) {
  return request.get('/customers', { params }).then(res => res.data.data as CustomerListResponse)
}

// 客户下拉选项（全量，不分页，仅启用客户；includeLeads 时混入未转化线索）
export function getCustomerOptions(includeLeads?: boolean) {
  return request.get('/customers/select/list', { params: includeLeads ? { includeLeads: 1 } : {} })
    .then(res => res.data as { code: number; success: boolean; message: string; data: Array<{ id: number | string; name: string; phone?: string; isLead?: boolean }> })
}

// 报价开单场景：线索快速转客户（幂等）
export function convertLeadToCustomer(leadId: number) {
  return request.post('/customers/select/convert-lead', { leadId }).then(res => {
    clearDictionaryCache('customer')
    return res.data as { code: number; success: boolean; message: string; data: { id: number; name: string; phone?: string } }
  })
}

export function getCustomerById(id: number) {
  return request.get(`/customers/${id}`).then(res => res.data as CustomerDetail)
}

// 客户欠款余额（开单选择客户时展示；无 customer:balance:read 权限后端返回 balance:null）
export function getCustomerBalance(id: number) {
  return request.get(`/customers/${id}/balance`)
    .then(res => res.data as { code: number; success: boolean; message: string; data: { balance: number | null } })
}

export function createCustomer(data: Partial<Customer>) {
  return request.post('/customers', data).then(res => {
    clearDictionaryCache('customer')
    return res.data
  })
}

export function updateCustomer(id: number, data: Partial<Customer>) {
  return request.put(`/customers/${id}`, data).then(res => {
    clearDictionaryCache('customer')
    return res.data
  })
}

export function deleteCustomer(id: number) {
  return request.delete(`/customers/${id}`).then(res => {
    clearDictionaryCache('customer')
    return res.data
  })
}

// 恢复已停用/删除的客户（唯一字段占用冲突时引导恢复）
export function restoreCustomer(id: number) {
  return request.post(`/customers/${id}/restore`).then(res => {
    clearDictionaryCache('customer')
    return res.data
  })
}

export function addFollowUp(id: number, data: Partial<FollowUp>) {
  return request.post(`/customers/${id}/follow-ups`, data).then(res => res.data)
}

export function getFollowUpList(customerId: number, params?: { page?: number; pageSize?: number; type?: string }) {
  return request.get(`/customers/${customerId}/follow-ups`, { params }).then(res => res.data.data)
}

export function updateFollowUp(customerId: number, followUpId: number, data: Partial<FollowUp>) {
  return request.put(`/customers/${customerId}/follow-ups/${followUpId}`, data).then(res => res.data)
}

export function deleteFollowUp(customerId: number, followUpId: number) {
  return request.delete(`/customers/${customerId}/follow-ups/${followUpId}`).then(res => res.data)
}

export function transferCustomerOwner(id: number, data: { employeeId: number; reason?: string }) {
  return request.post(`/customers/${id}/transfer-owner`, data).then(res => res.data)
}

export function getCustomerOwnerHistory(id: number) {
  return request.get(`/customers/${id}/owner-history`).then(res => res.data)
}
